import { useState } from "react";
import { UserPlus, Pencil, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import { UserDialog, PasswordDialog } from "./UserDialogs";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export const UsersTab = ({ users, reload }) => {
  const { user: me } = useAuth();
  const [edit, setEdit] = useState(undefined);
  const [pw, setPw] = useState(null);

  const toggle = async (u, active) => {
    try {
      await api.patch(`/users/${u.id}`, { active });
      toast.success(active ? "User activated" : "User deactivated");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="glass rounded-3xl overflow-hidden" data-testid="users-tab">
      <div className="flex items-center justify-between p-5">
        <div>
          <h3 className="text-lg font-bold text-emerald-950">Team accounts</h3>
          <p className="text-xs text-slate-500">{users.length} users</p>
        </div>
        <button data-testid="add-user-button" onClick={() => setEdit(null)} className="h-10 px-4 rounded-full bg-emerald-900 text-amber-50 text-sm font-semibold hover:bg-emerald-800 flex items-center gap-2 transition-colors">
          <UserPlus className="h-4 w-4" /> Add user
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead><tr className="bg-emerald-950/[0.03] text-left">{["Name", "Email", "Role", "Devices", "Active", "Actions"].map((h) => <th key={h} className="px-5 py-3 field-label">{h}</th>)}</tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-emerald-900/5">
                <td className="px-5 py-3 font-semibold text-emerald-950">{u.name}{u.id === me?.id && <span className="ml-2 text-[10px] rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">You</span>}</td>
                <td className="px-5 py-3 text-slate-600">{u.email}</td>
                <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${u.role === "admin" ? "bg-emerald-900 text-amber-50" : "bg-slate-100 text-slate-700"}`}>{u.role === "admin" ? "Management" : "Feedback Taker"}</span></td>
                <td className="px-5 py-3 text-xs text-slate-600">{u.devices.approved} approved · <span className={u.devices.pending ? "font-bold text-amber-700" : ""}>{u.devices.pending} pending</span></td>
                <td className="px-5 py-3"><Switch data-testid={`user-active-switch-${u.id}`} checked={u.active} disabled={u.id === me?.id} onCheckedChange={(v) => toggle(u, v)} /></td>
                <td className="px-5 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`edit-user-${u.id}`} onClick={() => setEdit(u)} className="inline-flex items-center gap-1 rounded-full border border-emerald-900/15 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-900 hover:bg-emerald-50 transition-colors"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                    <button data-testid={`change-password-${u.id}`} onClick={() => setPw(u)} className="inline-flex items-center gap-1 rounded-full border border-emerald-900/15 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-900 hover:bg-emerald-50 transition-colors"><KeyRound className="h-3.5 w-3.5" /> Password</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UserDialog open={edit !== undefined} user={edit} onClose={() => setEdit(undefined)} onSaved={reload} />
      <PasswordDialog user={pw} onClose={() => setPw(null)} />
    </div>
  );
};
